/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Přibyl (pribyl@cadwork.cz)
 *
 */

import Factory, {TElementId, TGroupId} from "./factory";
import {RenderDocument, Model, RenderElement, Debug} from "js_renderer";
import IScene from "../scene_interface";
import IModel from "../structural_elements/model_interface";

export interface IRenderDocumentProperties {
    renderModel: Model;
    renderElement: RenderElement;
    groupID: TGroupId;
}

export default
abstract class AbstractElement {

    protected _sceneAPI: IScene;
    protected _renderDocument: RenderDocument;
    protected _UID: TElementId;
    protected _typeName: string;
    protected _renderDocumentProperties: IRenderDocumentProperties;
    private _visible: boolean;

    constructor(aRenderDocument: RenderDocument) {
        this._renderDocument = aRenderDocument;
        this._sceneAPI = aRenderDocument.getSceneAPI();
        this._UID = Factory.getNewGUID();
        this._visible = true;
        this._typeName = "AbstractElement"; // the type name is taken from cadwork.webgl.model.elements.EEmbededElementTypes

        this._renderDocumentProperties = {
            renderModel: null,
            renderElement: null,
            groupID: 0
        };
    }

    public getTypeName(): string {
        return this._typeName;
    }

    public getUID(): TElementId {
        return this._UID;
    }

    public getModel(): IModel {
        return this._renderDocumentProperties.renderModel.getSceneAPIModel();
    }

    public getGroupID(): TGroupId {
        return this._renderDocumentProperties.groupID;
    }

    public attachToRenderElement(aRenderElement: RenderElement, aRenderModel: Model) {
        this._renderDocumentProperties.renderElement = aRenderElement;
        this._renderDocumentProperties.renderModel =  aRenderModel;
        this._renderDocumentProperties.groupID = aRenderElement.getGroup().getID();

        aRenderElement.setSceneAPIElementReference(this);
    }

    public attachElementToRenderModel(aModel: Model) {
        this._renderDocumentProperties.renderModel = aModel;
    }

    public attachToRenderGroup(aGroupID: number) {
        this._renderDocumentProperties.groupID = aGroupID;
    }

    /***
     * This method can be used to compare the content of two elements as e.g. in case of picking, we cannot compare the
     * elements UIDs, because everytime a new object is pocked a new element is created so they have never the same UID if you
     * pick the same model object covered by the element twice.
     * @param aAbstractElement Element to compare
     * @returns {boolean} True if the model objects in both elements have the same UID
     */
    public compareContent(aAbstractElement: AbstractElement): boolean {
        return this._renderDocumentProperties.renderElement.getUID() === aAbstractElement._renderDocumentProperties.renderElement.getUID();
    }

    public draw(aAddToRenderer: boolean) {
        //
    }

    public remove(): boolean {
        if (!this._renderDocumentProperties.renderElement) {
            // remove only valid element from document
            return false;
        }

        this._renderDocumentProperties.renderModel.removeElement(this._renderDocumentProperties.renderElement);

        this._renderDocumentProperties.renderElement = null;
        this._renderDocumentProperties.renderModel = null;
        this._renderDocumentProperties.groupID = 0;

        return true;
    }

    public setVisibility(aValue: boolean): boolean {
        if (!this._renderDocumentProperties.renderElement) {
            return false;
        }

        this._visible = aValue;
        this._renderDocumentProperties.renderElement.setVisibility(aValue);
    }

    public getVisibility(): boolean {
        if (!this._renderDocumentProperties.renderElement) {
            return this._visible;
        }

        return this._renderDocumentProperties.renderElement.getVisibility();
    }

    public abstract focus(aFocusFactor: number, aAnimationTime: number);

    public select(aValue: boolean): boolean {
        // TB: is this check really needed?
        if (!this._renderDocumentProperties.renderModel) {
            Debug.logInfo("Select failed: Render model is not set for the scene api element");
            return false;
        }

        if (!this._renderDocumentProperties.renderElement) {
            Debug.logInfo("Select failed: Render element is not set for the scene api element");
            return false;
        }

        this._renderDocumentProperties.renderElement.setSelected(aValue !== undefined ? aValue : true);
        this._sceneAPI.viewModeModule.scheduleRedraw();

        return true;
    }

    public unselect(): boolean {
        if (!this._renderDocumentProperties.renderModel) {
            Debug.logInfo("Unselect failed: Render model is not set for the scene api element");
            return false;
        }

        this.select(false);
    }

    public isSelected(): boolean {
        return this._renderDocumentProperties.renderElement.getIsSelected();
    }

    /***
     * Update positions of the geometry objects (shapes) in the element
     * @param {Array of Float32 arrays} aPositions New positions
     * @returns {boolean}
     */
    public updatePositions(aPositionsArray: Float32Array[]|number[][]) {
        if (!this._renderDocumentProperties.renderElement) {
            return false;
        }

        let geometryObjects = this._renderDocumentProperties.renderElement.getGeometryObjects();
        for (let i = 0; i < geometryObjects.length; i++) {
            let geometryObject = geometryObjects[i];
            if (geometryObject.getIsReadyToRender()) {
                geometryObject.updatePositions(aPositionsArray[i]);
            } else {
                return false;
            }
        }

        return true;
    }

    public getIsReadyToRender(): boolean {
        if (!this._renderDocumentProperties.renderElement) {
            return false;
        }

        return this._renderDocumentProperties.renderElement.getIsReadyToRender();
    }

    public getElementsInGroup(): AbstractElement[] {
        // observe group of current model element
        let group = this._renderDocumentProperties.renderElement.getGroup();
        if (!group) {
            return [];
        }

        // return all elements within the observed group (withut children) as scene api elements
        let sapiElements = [];
        let inGroupElements = group.getElements();
        for (let key in inGroupElements) {
            let renderElement = inGroupElements[key];
            let sapiElement: any = renderElement.getSceneAPIElementReference();
            if (!sapiElement) {
                sapiElement = this._sceneAPI.getDocument().createAPIElement(renderElement.getType());
                sapiElement.attachToRenderElement(renderElement, this._renderDocumentProperties.renderModel);
            }

            sapiElements.push(sapiElement);
        }

        return sapiElements;
    }

    public getAnnotations(aType: string) {
        let annotations = [];
        if (!this._renderDocumentProperties.renderElement) {
            Debug.logInfo("Get annotations failed: Render element is not set for the scene api element");
            return annotations;
        }

        if (aType === 'Metadata') {
            annotations = this._renderDocumentProperties.renderElement.getMetadata();
        }
        return annotations;
    }

    protected abstract _generateElementDescriptionJSON();
    protected abstract _generateGeometryObjectDescriptionJSON();
}
